import { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { supabase } from "./supabaseClient";
import AuthPage from "./AuthPage";
import BetaAccessForm from "./BetaAccessForm";
import ResetPassword from './ResetPassword';
import Dashboard from "./Dashboard";
import PrivacyWizard from './PrivacyWizard';
import PrivacyPage from './PrivacyPage';
import FeedPage from "./FeedPage";
import ComingSoon from "./ComingSoon";
import VetSkillEngine from "./VetSkillEngine";
import JobsPage from './JobsPage';
import EmployersPage from './EmployersPage';
import EmployerAuthPage from './EmployerAuthPage';
import EmployerAccountChoice from './EmployerAccountChoice';
import EmployerDashboard from './EmployerDashboard';
import NetworkPage from './NetworkPage';
import ForumPage from './ForumPage';
import ForumThreadPage from './ForumThreadPage';
import SettingsPage from './SettingsPage';
import MessagesPage from './MessagesPage';
import { ProfileProvider } from './ProfileContext';
import { EmployerProfileProvider } from './EmployerProfileContext';
import { ThemeProvider } from './ThemeContext';
import './theme.css';

export default function App() {
  const [session, setSession] = useState(null);
  const [accountType, setAccountType] = useState(null); // 'veteran' | 'employer' | null
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function resolveSession(nextSession) {
      setSession(nextSession);
      if (nextSession?.user) {
        const { data } = await supabase.rpc('get_account_type');
        setAccountType(data || null);
      } else {
        setAccountType(null);
      }
      setLoading(false);
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      resolveSession(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      resolveSession(session);
      // SIGNED_IN also fires when an existing session is recovered.
      // Route guards handle sign-in pages; keep homepage navigation user-driven.
    });

    // Employer signup creates the auth user (which fires SIGNED_IN and
    // resolves account type) BEFORE the redeem_employer_invite_code RPC
    // has created the employer_profiles row that makes get_account_type()
    // return 'employer'. EmployerAuthPage dispatches this once redemption
    // actually completes, so we re-resolve instead of leaving accountType
    // stuck at null for an already-authenticated session.
    const handleAccountTypeRefresh = () => {
      supabase.auth.getSession().then(({ data: { session } }) => resolveSession(session));
    };
    window.addEventListener('vq:refresh-account-type', handleAccountTypeRefresh);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('vq:refresh-account-type', handleAccountTypeRefresh);
    };
  }, []);

  if (loading) return (
    <div style={{
      background: "#0B1F3A",
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "'DM Sans', sans-serif",
      color: "#C9A84C",
      fontSize: "1rem",
    }}>
      Loading…
    </div>
  );

  const isVeteran = session && accountType === 'veteran';
  const isEmployer = session && accountType === 'employer';

  return (
    <ThemeProvider>
      <Router>
        <ProfileProvider>
          {isVeteran && <PrivacyWizard userId={session.user.id} />}
          <Routes>
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/"            element={<ComingSoon />} />
            <Route path="/auth"        element={
              isEmployer ? <Navigate to="/employer/dashboard" /> :
              isVeteran  ? <Navigate to="/feed" /> :
              session ? <BetaAccessForm user={session.user} /> : <AuthPage />
            } />
            <Route path="/feed"        element={isVeteran ? <FeedPage />     : <Navigate to={isEmployer ? "/employer/dashboard" : "/auth"} />} />
            <Route path="/dashboard"   element={isVeteran ? <Dashboard />    : <Navigate to={isEmployer ? "/employer/dashboard" : "/auth"} />} />
            <Route path="/network"     element={isVeteran ? <NetworkPage />  : <Navigate to={isEmployer ? "/employer/dashboard" : "/auth"} />} />
            <Route path="/forums"      element={isVeteran ? <ForumPage />    : <Navigate to={isEmployer ? "/employer/dashboard" : "/auth"} />} />
            <Route path="/forums/:postId" element={isVeteran ? <ForumThreadPage /> : <Navigate to={isEmployer ? "/employer/dashboard" : "/auth"} />} />
            <Route path="/messages"    element={(isVeteran || isEmployer) ? <MessagesPage /> : <Navigate to="/auth" />} />
            <Route path="/settings"    element={isVeteran ? <SettingsPage /> : <Navigate to={isEmployer ? "/employer/dashboard" : "/auth"} />} />
            <Route path="/skill-engine" element={<VetSkillEngine />} />
            <Route path="/jobs"        element={<JobsPage />} />
            <Route path="/employers"   element={<EmployersPage />} />
            <Route path="/employer/auth" element={
              isVeteran  ? <EmployerAccountChoice /> :
              isEmployer ? <Navigate to="/employer/dashboard" /> :
              session ? <BetaAccessForm user={session.user} employer /> : <EmployerAuthPage />
            } />
            <Route path="/employer/dashboard" element={
              isEmployer ? (
                <EmployerProfileProvider>
                  <EmployerDashboard />
                </EmployerProfileProvider>
              ) : <Navigate to={isVeteran ? "/dashboard" : "/employer/auth"} />
            } />
          </Routes>
        </ProfileProvider>
      </Router>
    </ThemeProvider>
  );
}
